import React, { useState, useEffect } from 'react';
import { 
  Send, Mail, MessageCircle, Users, Filter, Clock, 
  Loader2, CheckCircle, XCircle, Calendar, Radio,
  ChevronDown, ChevronUp, Eye, AlertCircle, BarChart3,
  Plus, Trash2, RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('rooftrack_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BroadcastCenter = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('compose'); // 'compose' or 'history'
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Compose state
  const [name, setName] = useState('');
  const [messageType, setMessageType] = useState('email'); // 'email', 'sms', 'both'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [smsMessage, setSmsMessage] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'status', 'custom'
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [scheduleType, setScheduleType] = useState('now'); // 'now' or 'later'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  
  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // History state
  const [broadcasts, setBroadcasts] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedBroadcast, setExpandedBroadcast] = useState(null);
  
  // Status options
  const statusOptions = [
    { value: 'new', label: 'New Leads', color: 'bg-blue-100 text-blue-700' },
    { value: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-700' },
    { value: 'quoted', label: 'Quoted', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'site_visit', label: 'Site Visit', color: 'bg-orange-100 text-orange-700' },
    { value: 'negotiating', label: 'Negotiating', color: 'bg-pink-100 text-pink-700' },
    { value: 'won', label: 'Won', color: 'bg-green-100 text-green-700' },
    { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-700' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadBroadcasts();
      loadStats();
    }
  }, [user]);

  const loadBroadcasts = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/broadcasts`,
        { headers: getAuthHeaders() }
      );
      setBroadcasts(response.data.broadcasts || []);
    } catch (err) {
      console.error('Failed to load broadcasts:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/broadcasts/stats`,
        { headers: getAuthHeaders() }
      );
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/broadcasts/preview`,
        {
          filter_type: filterType,
          lead_statuses: filterType === 'status' ? selectedStatuses : null
        },
        { headers: getAuthHeaders() }
      );
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (err) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleStatusToggle = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const sendBroadcast = async () => {
    // Validation
    if (!name.trim()) {
      toast.error('Please enter a broadcast name');
      return;
    }
    
    if (messageType === 'email' || messageType === 'both') {
      if (!emailSubject.trim() || !emailBody.trim()) {
        toast.error('Please fill in email subject and body');
        return;
      }
    }
    
    if (messageType === 'sms' || messageType === 'both') {
      if (!smsMessage.trim()) {
        toast.error('Please fill in SMS message');
        return;
      }
    }
    
    if (filterType === 'status' && selectedStatuses.length === 0) {
      toast.error('Please select at least one lead status');
      return;
    }
    
    setSending(true);
    try {
      let scheduled_at = null;
      if (scheduleType === 'later' && scheduledDate && scheduledTime) {
        scheduled_at = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/broadcasts`,
        {
          name,
          message_type: messageType,
          email_subject: emailSubject || null,
          email_body: emailBody || null,
          sms_message: smsMessage || null,
          filter_type: filterType,
          lead_statuses: filterType === 'status' ? selectedStatuses : null,
          scheduled_at
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(
          scheduleType === 'later' 
            ? 'Broadcast scheduled successfully!' 
            : 'Broadcast sent successfully!'
        );
        
        // Reset form
        setName('');
        setEmailSubject('');
        setEmailBody('');
        setSmsMessage('');
        setFilterType('all');
        setSelectedStatuses([]);
        setScheduleType('now');
        setScheduledDate('');
        setScheduledTime('');
        setShowPreview(false);
        setPreviewData(null);
        
        // Refresh data
        loadBroadcasts();
        loadStats();
        setActiveTab('history');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const cancelBroadcast = async (broadcastId) => {
    if (!confirm('Are you sure you want to cancel this scheduled broadcast?')) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/broadcasts/${broadcastId}/cancel`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Broadcast cancelled');
      loadBroadcasts();
      loadStats();
    } catch (err) {
      toast.error('Failed to cancel broadcast');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
      scheduled: { color: 'bg-blue-100 text-blue-700', label: 'Scheduled' },
      processing: { color: 'bg-purple-100 text-purple-700', label: 'Sending...' },
      completed: { color: 'bg-green-100 text-green-700', label: 'Completed' },
      cancelled: { color: 'bg-gray-100 text-gray-700', label: 'Cancelled' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Failed' }
    };
    const { color, label } = statusMap[status] || { color: 'bg-gray-100', label: status };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>{label}</span>;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="broadcast-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="w-7 h-7 text-purple-600" />
            Broadcast Center
          </h1>
          <p className="text-gray-600 mt-1">
            Send on-demand emails and SMS messages to your customers
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Send className="w-5 h-5" />
              <span className="text-sm">Total Broadcasts</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_broadcasts}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <Clock className="w-5 h-5" />
              <span className="text-sm">Scheduled</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{stats.scheduled_broadcasts}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Mail className="w-5 h-5" />
              <span className="text-sm">Emails Sent</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{stats.total_emails_sent}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex items-center gap-2 text-purple-600 mb-2">
              <MessageCircle className="w-5 h-5" />
              <span className="text-sm">SMS Sent</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">{stats.total_sms_sent}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('compose')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-[2px] ${
            activeTab === 'compose'
              ? 'text-purple-600 border-purple-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
          data-testid="compose-tab"
        >
          <Plus className="w-4 h-4" />
          New Broadcast
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-[2px] ${
            activeTab === 'history'
              ? 'text-purple-600 border-purple-600'
              : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
          data-testid="history-tab"
        >
          <BarChart3 className="w-4 h-4" />
          History
          {broadcasts.length > 0 && (
            <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
              {broadcasts.length}
            </span>
          )}
        </button>
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <div className="grid grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="col-span-2 space-y-6">
            {/* Broadcast Name */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Broadcast Details</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Broadcast Name *
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Holiday Promotion, Monthly Newsletter"
                  data-testid="broadcast-name"
                />
                <p className="text-xs text-gray-500 mt-1">For your reference only</p>
              </div>
            </div>

            {/* Message Type */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Message Type</h3>
              <div className="flex gap-3">
                {[
                  { value: 'email', label: 'Email Only', icon: Mail, color: 'blue' },
                  { value: 'sms', label: 'SMS Only', icon: MessageCircle, color: 'green' },
                  { value: 'both', label: 'Email + SMS', icon: Send, color: 'purple' }
                ].map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    onClick={() => setMessageType(value)}
                    className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
                      messageType === value
                        ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`message-type-${value}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Email Content */}
            {(messageType === 'email' || messageType === 'both') && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Email Content
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject Line *
                    </label>
                    <Input
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Email subject..."
                      data-testid="email-subject"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Body *
                    </label>
                    <textarea
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                      rows={8}
                      placeholder="Write your email... Use {{customer_name}} and {{company_name}} for personalization"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="email-body"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Variables: {'{{customer_name}}'}, {'{{company_name}}'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* SMS Content */}
            {(messageType === 'sms' || messageType === 'both') && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  SMS Content
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMS Message * ({smsMessage.length}/160 chars recommended)
                  </label>
                  <textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    rows={4}
                    placeholder="Write your SMS... Keep it under 160 chars for best delivery"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    data-testid="sms-message"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Variables: {'{{customer_name}}'}, {'{{company_name}}'}. Max 1600 chars.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Recipients */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Recipients
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setFilterType('all')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    filterType === 'all'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="filter-all"
                >
                  <input type="radio" checked={filterType === 'all'} readOnly />
                  <span>All Customers</span>
                </button>
                
                <button
                  onClick={() => setFilterType('status')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    filterType === 'status'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="filter-status"
                >
                  <input type="radio" checked={filterType === 'status'} readOnly />
                  <span>Filter by Lead Status</span>
                </button>
              </div>
              
              {/* Status Filter Options */}
              {filterType === 'status' && (
                <div className="mt-4 space-y-2">
                  {statusOptions.map(({ value, label, color }) => (
                    <label
                      key={value}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedStatuses.includes(value) ? 'bg-gray-100' : 'hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedStatuses.includes(value)}
                        onChange={() => handleStatusToggle(value)}
                        className="rounded"
                      />
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              
              {/* Preview Button */}
              <Button
                variant="outline"
                className="w-full mt-4"
                onClick={loadPreview}
                disabled={previewLoading}
                data-testid="preview-recipients-btn"
              >
                {previewLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview Recipients
              </Button>
              
              {/* Preview Results */}
              {showPreview && previewData && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Recipients Summary</h4>
                  <div className="space-y-1 text-sm">
                    <p>Total: <span className="font-semibold">{previewData.total_customers}</span></p>
                    <p className="text-blue-600">With Email: <span className="font-semibold">{previewData.with_email}</span></p>
                    <p className="text-green-600">With Phone: <span className="font-semibold">{previewData.with_phone}</span></p>
                  </div>
                  {previewData.customers?.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-2">Preview (first {previewData.customers.length}):</p>
                      <div className="space-y-1">
                        {previewData.customers.map((c, i) => (
                          <div key={i} className="text-xs text-gray-600">
                            {c.name || 'Unknown'} - {c.email || c.phone || 'No contact'}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Scheduling */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Scheduling
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={() => setScheduleType('now')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    scheduleType === 'now'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="schedule-now"
                >
                  <input type="radio" checked={scheduleType === 'now'} readOnly />
                  <div className="text-left">
                    <span className="font-medium">Send Now</span>
                    <p className="text-xs text-gray-500">Broadcast immediately</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setScheduleType('later')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg border transition-all ${
                    scheduleType === 'later'
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="schedule-later"
                >
                  <input type="radio" checked={scheduleType === 'later'} readOnly />
                  <div className="text-left">
                    <span className="font-medium">Schedule for Later</span>
                    <p className="text-xs text-gray-500">Choose date and time</p>
                  </div>
                </button>
              </div>
              
              {/* Schedule Date/Time */}
              {scheduleType === 'later' && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <Input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="schedule-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <Input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      data-testid="schedule-time"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Send Button */}
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700"
              size="lg"
              onClick={sendBroadcast}
              disabled={sending}
              data-testid="send-broadcast-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  {scheduleType === 'later' ? 'Scheduling...' : 'Sending...'}
                </>
              ) : (
                <>
                  {scheduleType === 'later' ? (
                    <>
                      <Calendar className="w-5 h-5 mr-2" />
                      Schedule Broadcast
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Broadcast
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {broadcasts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Send className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="font-semibold text-gray-900 mb-2">No broadcasts yet</h3>
              <p className="text-gray-500 mb-4">Create your first broadcast to reach your customers</p>
              <Button onClick={() => setActiveTab('compose')} data-testid="create-first-broadcast">
                <Plus className="w-4 h-4 mr-2" />
                Create Broadcast
              </Button>
            </div>
          ) : (
            broadcasts.map((broadcast) => (
              <div 
                key={broadcast.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                data-testid={`broadcast-item-${broadcast.id}`}
              >
                {/* Broadcast Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedBroadcast(expandedBroadcast === broadcast.id ? null : broadcast.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      broadcast.message_type === 'email' ? 'bg-blue-100' :
                      broadcast.message_type === 'sms' ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {broadcast.message_type === 'email' ? (
                        <Mail className="w-5 h-5 text-blue-600" />
                      ) : broadcast.message_type === 'sms' ? (
                        <MessageCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Send className="w-5 h-5 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{broadcast.name}</div>
                      <div className="text-sm text-gray-500">
                        {new Date(broadcast.created_at).toLocaleDateString()} at{' '}
                        {new Date(broadcast.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      {broadcast.message_type !== 'sms' && (
                        <div className="text-blue-600">
                          <span className="font-semibold">{broadcast.emails_sent}</span>/{broadcast.total_email_recipients} emails
                        </div>
                      )}
                      {broadcast.message_type !== 'email' && (
                        <div className="text-green-600">
                          <span className="font-semibold">{broadcast.sms_sent}</span>/{broadcast.total_sms_recipients} SMS
                        </div>
                      )}
                    </div>
                    {getStatusBadge(broadcast.status)}
                    {broadcast.status === 'scheduled' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); cancelBroadcast(broadcast.id); }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {expandedBroadcast === broadcast.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedBroadcast === broadcast.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-6 mt-4">
                      {/* Email Content */}
                      {broadcast.email_subject && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-600" />
                            Email Content
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="font-medium text-sm">{broadcast.email_subject}</p>
                            <p className="text-sm text-gray-600 mt-2 line-clamp-4">{broadcast.email_body}</p>
                          </div>
                          <div className="mt-2 flex gap-4 text-sm">
                            <span className="text-green-600">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              {broadcast.emails_sent} sent
                            </span>
                            {broadcast.emails_failed > 0 && (
                              <span className="text-red-600">
                                <XCircle className="w-4 h-4 inline mr-1" />
                                {broadcast.emails_failed} failed
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* SMS Content */}
                      {broadcast.sms_message && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-green-600" />
                            SMS Content
                          </h4>
                          <div className="bg-green-50 rounded-lg p-3">
                            <p className="text-sm text-gray-700">{broadcast.sms_message}</p>
                          </div>
                          <div className="mt-2 flex gap-4 text-sm">
                            <span className="text-green-600">
                              <CheckCircle className="w-4 h-4 inline mr-1" />
                              {broadcast.sms_sent} sent
                            </span>
                            {broadcast.sms_failed > 0 && (
                              <span className="text-red-600">
                                <XCircle className="w-4 h-4 inline mr-1" />
                                {broadcast.sms_failed} failed
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Scheduled Info */}
                    {broadcast.status === 'scheduled' && broadcast.scheduled_at && (
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="text-blue-700">
                          Scheduled for: {new Date(broadcast.scheduled_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
          
          {/* Refresh Button */}
          {broadcasts.length > 0 && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={loadBroadcasts}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BroadcastCenter;
